import { useCallback, useEffect, useState } from 'react';
import { Warning } from 'shared/icons';
import api from '../../../services/api';
import Badge from '../../ui/Badge';
import { DateTimeText, Id } from '../../ui/Mono';
import { Skeleton } from '../../ui/Skeleton';
import { FormatLocationPlain } from '../collector/collectorScan';
import { IncidentLocationOf } from '../IncidentDetailModal';
import { ExtractListRows } from '../payload';
import { FormatLpnProductCount } from '../warehouseLabels';
import { INCIDENT_LOCATION_TYPE } from './serviceOrderModel';

/**
 * A RAIA DE INCIDENTES do quadro (§8.1.5).
 *
 * O PRD pede duas coisas na mesma faixa, e elas são diferentes de propósito:
 *
 *  1. **os incidentes ABERTOS** — a divergência registrada, com dono e
 *     histórico (a fila que a página /warehouse/incidents conduz);
 *  2. **o que está PARADO nas posições `INC-*`** — carga física encostada no
 *     canto do galpão. Um pallet pode estar lá com o incidente dele já
 *     resolvido no papel e ninguém ter ido buscá-lo: o quadro do Supervisor é
 *     o lugar onde essa carga esquecida volta a existir.
 *
 * ── Degradação por permissão ────────────────────────────────────────────────
 * Cada bloco depende de uma permissão granular diferente (`wms:manage:
 * incidents`, `:locations`, `:lpns`). Quem não tem uma delas simplesmente não
 * vê aquele bloco — a raia NUNCA escreve "nenhum incidente" para quem apenas
 * não pode enxergá-los. Estado vazio falso é pior que bloco ausente.
 */
const IncidentsLane = ({ label, canReadIncidents, canReadLocations, canReadLpns, refreshToken = 0, onOpenIncident }) => {
    const [incidents, setIncidents] = useState([]);
    const [parked, setParked] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            if (canReadIncidents) {
                const data = await api.getWarehouseIncidents({ status: 'OPEN', limit: 50 });
                setIncidents(ExtractListRows(data, ['incidents']));
            } else {
                setIncidents([]);
            }

            if (canReadLocations && canReadLpns) {
                const locationsPayload = await api.getWarehouseLocations({
                    type: INCIDENT_LOCATION_TYPE,
                    limit: 100,
                });
                const locations = ExtractListRows(locationsPayload, ['locations']);
                const withContent = await Promise.all(
                    locations.map(async (location) => {
                        const lpnPayload = await api.getWarehouseLpns({ location_id: location.id, limit: 50 });
                        return { location, lpns: ExtractListRows(lpnPayload, ['lpns']) };
                    }),
                );
                setParked(withContent);
            } else {
                setParked([]);
            }
        } catch (error) {
            setLoadError(error?.message || label('wms.common.load_error'));
        } finally {
            setLoading(false);
        }
    }, [canReadIncidents, canReadLocations, canReadLpns, label]);

    useEffect(() => {
        load();
    }, [load, refreshToken]);

    return (
        <section className="rounded-xl border border-warning-300 bg-warning-soft/40 p-3 dark:border-warning-800 dark:bg-warning-900/10">
            <header className="mb-3 flex flex-wrap items-center gap-2">
                <Warning size={18} className="text-warning-700 dark:text-warning-400" />
                <h2 className="text-sm font-semibold uppercase tracking-wide text-warning-800 dark:text-warning-300">
                    {label('wms.kanban.incidents')}
                </h2>
                {incidents.length > 0 ? <Badge variant="warning">{incidents.length}</Badge> : null}
                <p className="w-full text-xs text-mono-600 dark:text-mono-400">{label('wms.kanban.incidents_help')}</p>
            </header>

            {loadError ? (
                <p className="text-sm text-danger-700 dark:text-danger-400">{loadError}</p>
            ) : loading ? (
                <div className="flex gap-3">
                    <Skeleton className="h-20 w-64" />
                    <Skeleton className="h-20 w-64" />
                </div>
            ) : (
                <div className="flex gap-3 overflow-x-auto pb-1">
                    {canReadIncidents && incidents.length === 0 ? (
                        <p className="text-sm text-mono-600 dark:text-mono-400">{label('wms.incidents.empty_open')}</p>
                    ) : null}

                    {incidents.map((incident) => (
                        <button
                            key={incident.id}
                            type="button"
                            onClick={() => onOpenIncident?.(incident)}
                            className="w-64 shrink-0 space-y-1 rounded-lg border border-mono-200 bg-white p-3 text-left transition-colors hover:border-warning-500 dark:border-mono-800 dark:bg-mono-950"
                        >
                            <div className="flex items-center justify-between gap-2">
                                <Badge variant="warning">{label(`wms.incident_kind.${incident.kind}`)}</Badge>
                                <DateTimeText value={incident.created_at} relative className="text-[11px]" />
                            </div>
                            {IncidentLocationOf(incident) ? (
                                <p className="truncate text-xs text-mono-600 dark:text-mono-400">
                                    <Id value={incident.location_code} className="text-[11px]" />
                                    <span className="ml-1">
                                        {FormatLocationPlain(IncidentLocationOf(incident), { label })}
                                    </span>
                                </p>
                            ) : null}
                            {incident.lpn_code ? <Id value={incident.lpn_code} className="text-[11px]" /> : null}
                            {incident.notes ? (
                                <p className="line-clamp-2 text-xs text-mono-500 dark:text-mono-400">{incident.notes}</p>
                            ) : null}
                        </button>
                    ))}

                    {/* Carga encostada nas posições de incidente: existe mesmo
                        quando o papel já foi resolvido. */}
                    {parked.map(({ location, lpns }) => (
                        <div
                            key={location.id}
                            className="w-64 shrink-0 space-y-1 rounded-lg border border-dashed border-mono-300 bg-white p-3 dark:border-mono-700 dark:bg-mono-950"
                        >
                            <Id value={location.code} className="text-xs font-semibold" />
                            {lpns.length === 0 ? (
                                <p className="text-xs text-mono-500 dark:text-mono-400">
                                    {label('wms.panel.no_lpns_here')}
                                </p>
                            ) : (
                                <ul className="space-y-0.5">
                                    {lpns.map((lpn) => (
                                        <li key={lpn.id} className="flex items-center justify-between gap-2">
                                            <Id value={lpn.code} className="text-[11px]" />
                                            <span className="text-[11px] text-mono-500 dark:text-mono-400">
                                                {FormatLpnProductCount(lpn.items_count ?? lpn.item_count, label)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
};

export default IncidentsLane;
